import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {IFlow} from "../../../../../data/interfaces/iflow.interface";
import {ApiFlowDataService} from "../../../../../data/services/api/flow/api-flow-data.service";
import {
  faArrowUpRightFromSquare,
  faCheck,
  faCircleExclamation,
  faCircleInfo,
  faClock,
  faGlobe,
  faRotateRight,
  faTriangleExclamation,
  faXmark
} from "@fortawesome/free-solid-svg-icons";
import {ESTADOS_LX} from "../../../../../shared/ui/liga-ui";

// Tiempo que se queda el «¡Gracias!» antes de regresar solo a la lista (igual que antes).
const ESPERA_REGRESO = 2000

interface IAvisoExterno {
  titulo: string
  texto: string
}

// Paso que se completa en otra página (liga de la configuración o la que genera
// el asesor). Mismo flujo de siempre: abrir la liga → «Ya terminé» →
// POST flowData/external → onEndProcess(true). Estilos en _liga-ui.scss y _liga-b.scss.
@Component({
  selector: 'app-external-component',
  templateUrl: './external-component.component.html',
  styleUrls: ['./external-component.component.scss']
})
export class ExternalComponentComponent implements OnInit, OnDestroy {
  @Input() data!: IFlow
  @Input() pushingId: string = ''
  @Output() onEndProcess = new EventEmitter()
  currentUrl = ''
  startedProcess = false
  settings = {description: '', img: '', url: ''}

  readonly estados = ESTADOS_LX
  readonly iconos = {
    externo: faArrowUpRightFromSquare,
    listo: faCheck,
    reloj: faClock,
    sitio: faGlobe,
    reintentar: faRotateRight,
    aviso: faTriangleExclamation,
    error: faCircleExclamation,
    ayuda: faCircleInfo,
    cerrar: faXmark
  }
  titulo = ''
  subtitulo = ''
  // Dominio de la liga («sitio.com») para que sepa a dónde va.
  dominio = ''
  imagenFallo = false
  enviando = false
  terminado = false
  aviso: IAvisoExterno | null = null

  private temporizadorRegreso: ReturnType<typeof setTimeout> | null = null
  private regresoEmitido = false

  constructor(
    private flowDataService: ApiFlowDataService,
    private host: ElementRef<HTMLElement>
  ) {
  }

  ngOnInit() {
    try {
      this.settings = {...this.settings, ...JSON.parse(this.data.properties.settings)}
    } catch {
      // Sin configuración legible se usa la liga que ya traiga el paso (data.value).
    }
    if (this.settings.url && this.settings.url !== '') this.data.value = this.settings.url
    this.titulo = this.data?.properties?.title || 'Proceso en otra página'
    this.subtitulo = this.data?.properties?.subtitle || 'Este paso se completa en una página aparte.'
    this.dominio = this.dominioDe(this.data?.value)
  }

  ngOnDestroy() {
    this.cancelarRegreso()
  }

  open() {
    if (this.data.value) {
      this.currentUrl = this.data.value
      this.startedProcess = true
      this.aviso = null
    }
  }

  get getLink() {
    return this.data.value
  }

  validateExternalLink() {
    if (this.enviando || this.terminado) return
    this.enviando = true
    this.aviso = null
    this.flowDataService.validateExternalUrl({idDocument: this.data.id, idStage: this.data.userDocumentId, pushingId: this.pushingId}).subscribe({
      next: r => {
        this.enviando = false
        if (r.error) {
          this.mostrarAviso('Aún no podemos continuar', r.msg || 'Revisa que hayas terminado en la otra página e inténtalo de nuevo.')
          return
        }
        this.terminado = true
        this.cancelarRegreso()
        this.temporizadorRegreso = setTimeout(() => this.emitirRegreso(), ESPERA_REGRESO)
      },
      // Antes un error sin respuesta dejaba la pantalla de carga encima para siempre.
      error: () => {
        this.enviando = false
        this.mostrarAviso('No pudimos confirmar tu proceso', 'Hubo un error de conexión. Inténtalo de nuevo en un momento.')
      }
    })
  }

  // Sin liga todavía: regresa a la lista, como el «Entiendo» de antes.
  entendido() {
    this.onEndProcess.emit(true)
  }

  continuar() {
    this.cancelarRegreso()
    this.emitirRegreso()
  }

  cerrarAviso() {
    this.aviso = null
  }

  private mostrarAviso(titulo: string, texto: string) {
    this.aviso = {titulo, texto}
    setTimeout(() => {
      const caja = this.host.nativeElement.querySelector<HTMLElement>('.lxb-aviso-error')
      if (!caja) return
      caja.scrollIntoView({block: 'center', behavior: this.movimientoReducido() ? 'auto' : 'smooth'})
      caja.focus({preventScroll: true})
    })
  }

  private dominioDe(url: string | undefined | null): string {
    if (!url) return ''
    try {
      return new URL(url).hostname.replace(/^www\./, '')
    } catch {
      return ''
    }
  }

  private emitirRegreso() {
    this.temporizadorRegreso = null
    if (this.regresoEmitido) return
    this.regresoEmitido = true
    this.onEndProcess.emit(true)
  }

  private cancelarRegreso() {
    if (this.temporizadorRegreso) clearTimeout(this.temporizadorRegreso)
    this.temporizadorRegreso = null
  }

  private movimientoReducido(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }
}
